import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BrandingV2Service } from '@galaxy/partner';
import {
  AgreementInterface,
  CommonFieldInterface,
  ConfigInterface,
  CustomFieldInterface,
  CustomerSalesOrdersService,
  FieldInterface,
  Order,
  SalesOrdersPublicService,
  UserInterface,
} from '@vendasta/sales-orders';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { AccountGroup } from '../account-group';
import { AppConfigService } from '../app-config.service';
import { Feature, PartnerMarketConfigService } from '../partner-market-config';
import { setTemporarySession } from '../temporary-sessions';

interface OrderKeySet {
  businessId: string;
  orderId: string;

  // TODO(ColinLarsonCA) remove this when the page `public/orders/:orderId` no longer receives traffic
  hashedOrderId: string;
}

@Injectable({ providedIn: 'root' })
export class OrdersService {
  private readonly requestReload$$ = new ReplaySubject<boolean>(1);
  readonly requestReload$ = this.requestReload$$.asObservable().pipe(startWith(true));
  private orderKeySet$$: ReplaySubject<OrderKeySet> = new ReplaySubject<OrderKeySet>(1);
  order$: Observable<Order>;
  orderConfig$: Observable<ConfigInterface>;
  partnerName$: Observable<string>;
  users$: Observable<{ [key: string]: UserInterface }>;

  constructor(
    public router: Router,
    private brandingService: BrandingV2Service,
    private salesOrdersService: CustomerSalesOrdersService,
    private publicSalesOrdersService: SalesOrdersPublicService,
    private configService: AppConfigService,
    private partnerMarketConfigService: PartnerMarketConfigService,
  ) {
    this.order$ = combineLatest([this.orderKeySet$$, this.requestReload$]).pipe(
      filter(([keySet, _reload]) => !!keySet),
      switchMap(([keySet, _reload]) => {
        return this.salesOrdersService.get(keySet.orderId, keySet.businessId);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.users$ = this.order$.pipe(
      switchMap((o) => this.salesOrdersService.getUsers(o.orderId, o.businessId)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.partnerName$ = this.order$
      .pipe(
        switchMap((order) => this.brandingService.getBranding(order.partnerId)),
        map((branding) => branding.name),
      )
      .pipe(shareReplay({ refCount: true, bufferSize: 1 }));

    this.orderConfig$ = this.orderKeySet$$.pipe(
      switchMap((keySet) => {
        return this.salesOrdersService.getConfig(keySet.businessId, keySet.orderId);
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  setOrderKeySet(keySet: OrderKeySet): void {
    this.orderKeySet$$.next(keySet);
  }

  setHashedOrderId(hashedOrderId: string): void {
    this.publicSalesOrdersService.get(hashedOrderId).subscribe((order) => {
      this.setOrderKeySet({ orderId: order.orderId, businessId: order.businessId, hashedOrderId: hashedOrderId });
    });
  }

  getLogoUrl(): Observable<string> {
    const business$ = this.order$.pipe(
      map(
        (order) =>
          new AccountGroup({
            partnerId: order.partnerId,
            marketId: order.marketId,
            accountGroupId: order.businessId,
          }),
      ),
    );
    const hasWhitelabel$ = this.partnerMarketConfigService.hasAccessToFeature(Feature.whitelabel).pipe(startWith(true));
    return combineLatest([this.configService.getMarketLogo(business$), hasWhitelabel$]).pipe(
      map(([marketLogo, hasWhitelabel]) => {
        if (!hasWhitelabel) {
          return 'https://vstatic-prod.apigateway.co/partner-center-client/assets/images/powered-by-vendasta-logo.png';
        }
        return marketLogo;
      }),
      distinctUntilChanged(),
    );
  }

  updateAnswers(
    customFields: CustomFieldInterface[],
    commonFields: CommonFieldInterface[],
    extraFields: FieldInterface[],
  ): Observable<string> {
    return this.orderKeySet$$.pipe(
      switchMap((keySet) => {
        return this.salesOrdersService.updateAnswers(
          keySet.orderId,
          keySet.businessId,
          customFields,
          commonFields,
          extraFields,
        );
      }),
    );
  }

  approve(agreements: AgreementInterface[], paymentMethodToken?: string): Observable<string> {
    return this.orderKeySet$$.pipe(
      switchMap((keySet) => {
        return this.salesOrdersService.approveSalesOrder(
          keySet.orderId,
          keySet.businessId,
          agreements,
          paymentMethodToken,
        );
      }),
    );
  }

  decline(declinedReason: string): Observable<Order> {
    return this.orderKeySet$$.pipe(
      switchMap((keySet) => {
        return this.salesOrdersService.declineSalesOrder(keySet.businessId, keySet.orderId, declinedReason);
      }),
    );
  }

  reloadOrder(): void {
    this.requestReload$$.next(true);
    const element = document.getElementById('top');
    element.scrollIntoView();
  }

  authenticateOrderRecipient(token: string): Observable<string> {
    return this.publicSalesOrdersService.getOrderRecipientSession(token).pipe(
      switchMap((sessionId) => {
        setTemporarySession(sessionId);
        return sessionId;
      }),
    );
  }
}
